import React, { useEffect, useRef, useState } from 'react';
import '../Pipeline.css';

/**
 * MermaidDiagram
 *
 * Dynamically loads mermaid from CDN (no npm install required) and renders
 * the supplied chart string as an SVG. Falls back to a styled code block if
 * the library cannot be loaded or the diagram has a syntax error.
 */

let mermaidReady = false;
let mermaidLoading = false;
const mermaidCallbacks = [];

/**
 * Normalize Mermaid strings the LLM sometimes generates with semicolons as
 * statement separators (e.g. "graph TD; A --> B; B --> C") instead of
 * the newlines Mermaid 10 requires.
 */
function sanitizeMermaid(raw) {
  if (!raw || typeof raw !== 'string') return '';
  let c = raw.trim();
  // Replace semicolons used as statement separators with newline + indent.
  c = c.replace(/;\s*/g, '\n    ');
  // After replacement the first line may have trailing spaces — trim them.
  c = c.replace(/^(\S[^\n]*)\s*\n/, '$1\n');
  // "->>" is sequence-diagram syntax; flowchart/graph needs "-->".
  if (/^(flowchart|graph)\b/i.test(c)) {
    c = c.replace(/->>?/g, '-->');
  }
  return c;
}

function loadMermaid(cb) {
  if (mermaidReady) { cb(true); return; }
  mermaidCallbacks.push(cb);
  if (mermaidLoading) return;
  mermaidLoading = true;

  const script = document.createElement('script');
  script.src = 'https://cdn.jsdelivr.net/npm/mermaid@10/dist/mermaid.min.js';
  script.onload = () => {
    try {
      window.mermaid.initialize({
        startOnLoad: false,
        theme: 'neutral',
        securityLevel: 'loose',
        fontFamily: 'Inter, system-ui, sans-serif',
      });
      mermaidReady = true;
      mermaidCallbacks.forEach((fn) => fn(true));
    } catch {
      mermaidCallbacks.forEach((fn) => fn(false));
    }
    mermaidCallbacks.length = 0;
  };
  script.onerror = () => {
    mermaidCallbacks.forEach((fn) => fn(false));
    mermaidCallbacks.length = 0;
  };
  document.head.appendChild(script);
}

let _idCounter = 0;

function MermaidDiagram({ chart, title }) {
  const containerRef = useRef(null);
  const idRef = useRef(`mermaid-d${++_idCounter}`);
  const [status, setStatus] = useState('loading'); // 'loading' | 'rendered' | 'error'
  const [errorMsg, setErrorMsg] = useState('');

  useEffect(() => {
    if (!chart) { setStatus('error'); setErrorMsg('No diagram data'); return; }

    let cancelled = false;

    const render = async () => {
      try {
        const clean = sanitizeMermaid(chart);
        const { svg } = await window.mermaid.render(idRef.current, clean);
        if (!cancelled && containerRef.current) {
          containerRef.current.innerHTML = svg;
          setStatus('rendered');
        }
      } catch (err) {
        if (!cancelled) {
          setStatus('error');
          setErrorMsg(err?.message || 'Diagram syntax error');
        }
      }
    };

    loadMermaid((ok) => {
      if (!cancelled) {
        if (ok) render();
        else { setStatus('error'); setErrorMsg('Could not load Mermaid library'); }
      }
    });

    return () => { cancelled = true; };
  }, [chart]);

  return (
    <div className="pipeline-mermaid-wrapper">
      {title && <div className="pipeline-mermaid-title">{title}</div>}

      {status === 'loading' && (
        <div className="pipeline-loading" style={{ padding: '30px 0' }}>
          <div className="pipeline-spinner" style={{ width: 28, height: 28 }} />
          <span>Rendering diagram…</span>
        </div>
      )}

      {status === 'error' && (
        <>
          <div className="pipeline-error" style={{ marginBottom: 10 }}>
            {errorMsg} — showing raw Mermaid source:
          </div>
          <pre className="pipeline-mermaid-fallback">{chart}</pre>
        </>
      )}

      <div
        ref={containerRef}
        className="pipeline-mermaid"
        style={{ display: status === 'rendered' ? 'block' : 'none' }}
      />
    </div>
  );
}

export default MermaidDiagram;
